import React from 'react';
import { FieldArrayWithId } from 'react-hook-form';
import { HStack, IconButton, Text, DataList, DataItem } from '@cad-arc';
import { EventNotificationsEntityIdProvider } from '../../../../event-notifications/event-notifications-entity-id-context';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { EntityPanelFormPersonFieldset } from '../../../../panels/entity-panels/forms/persons/entity-panel-person-fields';
import { useOpenEventPanel } from '../../../use-open-event-panel';
import { EntityPanelFormPersonBaseKey } from '../../../../panels/entity-panels/forms/types';
import { useEventMetadataStore } from '../../event-metadata';

export type EventPanelPersonFieldsetProps = {
    field: FieldArrayWithId<EventPanelFormType, 'reportingParties', 'rhfKey'>;
    index: number;
    remove: UseTrackedFieldArrayRemove;
    baseKey: EntityPanelFormPersonBaseKey;
    allowMembersControl?: boolean;
    // Hide the DEx Fields and use different expansion icon
    displayAsCards?: boolean;
    isReadOnly?: boolean;
    eventPanelOptions: EventPanelOptions;
    userManuallyAddedField?: boolean;
};

// Skip field reference comparison: RHF v7 recreates field objects for all items on
// append/remove. Field data is reactive via form.control subscriptions in child inputs;
// contextId and eventInfo are BE-assigned at load time and never change mid-session.
function arePropsEqual(
    prev: EventPanelPersonFieldsetProps,
    next: EventPanelPersonFieldsetProps
): boolean {
    return (
        prev.index === next.index &&
        prev.baseKey === next.baseKey &&
        prev.remove === next.remove &&
        prev.eventPanelOptions === next.eventPanelOptions &&
        prev.allowMembersControl === next.allowMembersControl &&
        prev.displayAsCards === next.displayAsCards &&
        prev.isReadOnly === next.isReadOnly &&
        prev.userManuallyAddedField === next.userManuallyAddedField
    );
}

export const EventPanelFormPersonFieldset = React.memo(function EventPanelFormPersonFieldset(
    props: EventPanelPersonFieldsetProps
): React.ReactElement {
    const {
        index,
        field,
        remove,
        eventPanelOptions,
        baseKey,
        displayAsCards,
        isReadOnly,
        allowMembersControl,
        userManuallyAddedField,
    } = props;
    const openEventPanel = useOpenEventPanel();
    const { isFirstResponder } = useApplicationType();
    const isMergedPerson =
        props.baseKey === 'mergedReportingParties' || props.baseKey === 'mergedInvolvedPersons';

    const mergedEventId = field?.eventInfo?.id;

    const onOpenMergedEvent = () => {
        if (mergedEventId) {
            openEventPanel({
                eventId: mergedEventId,
            });
        }
    };

    const contextId = field.contextId;
    const eventMetadata = useEventMetadataStore().state?.metadata;
    const eventNumber = eventMetadata?.agencyEventNumber;
    const eventId = eventMetadata?.eventId;

    return (
        <EventNotificationsEntityIdProvider entityId={contextId ?? undefined}>
            {/* Only show the merged text if these are actually merged reporting parties */}
            {field.eventInfo?.isChildEvent && isMergedPerson && !isFirstResponder ? (
                <DataList variant="stacked">
                    <DataItem dataKey="Merged Event">
                        <HStack>
                            <Text>{field?.eventInfo?.agencyEventNumber}</Text>
                            <IconButton
                                size="sm"
                                variant="ghost"
                                aria-label="open event"
                                icon="OpenNewWindow"
                                onClick={onOpenMergedEvent}
                            />
                        </HStack>
                    </DataItem>
                </DataList>
            ) : undefined}
            <EntityPanelFormPersonFieldset
                entityPanelOptions={eventPanelOptions}
                baseKey={baseKey}
                index={index}
                remove={remove}
                displayAsCards={displayAsCards}
                isReadOnly={isReadOnly}
                allowMembersControl={allowMembersControl}
                entityNumber={eventNumber}
                eventId={eventId ?? undefined}
                userManuallyAddedField={userManuallyAddedField}
            />
        </EventNotificationsEntityIdProvider>
    );
}, arePropsEqual);
